import React, {useCallback, useEffect, useRef, useState} from "react";
import {useParams} from "react-router-dom";
import API from "../services/api";
import DesignerCanvas from "../components/designer/DesignerCanvas";
import "./RuntimePage.css";

const MIN_ZOOM = 0.1;
const MAX_ZOOM = 4;
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const RuntimePage = () => {
    const {id} = useParams();

    const [page, setPage] = useState(null);
    const [objects, setObjects] = useState([]);
    const [latestTags, setLatestTags] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [zoom, setZoom] = useState(1);
    const [pan, setPan] = useState({x: 0, y: 0});
    const [viewportSize, setViewportSize] = useState({width: 0, height: 0});
    const [toolbarPosition, setToolbarPosition] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem("runtimeZoomToolbarPosition")) || null;
        } catch {
            return null;
        }
    });
    const viewportRef = useRef(null);
    const toolbarRef = useRef(null);
    const toolbarDragRef = useRef(null);
    const pointersRef = useRef(new Map());
    const gestureRef = useRef(null);
    const singlePanRef = useRef(null);
    const suppressClickRef = useRef(false);
    const fittedPageRef = useRef(null);

    const constrainPan = useCallback((nextPan, nextZoom) => {
        if (!page || !viewportSize.width || !viewportSize.height) return nextPan;
        const scaledWidth = Number(page.width || 1600) * nextZoom;
        const scaledHeight = Number(page.height || 900) * nextZoom;
        return {
            x: scaledWidth <= viewportSize.width
                ? (viewportSize.width - scaledWidth) / 2
                : clamp(nextPan.x, viewportSize.width - scaledWidth, 0),
            y: scaledHeight <= viewportSize.height
                ? (viewportSize.height - scaledHeight) / 2
                : clamp(nextPan.y, viewportSize.height - scaledHeight, 0),
        };
    }, [page, viewportSize]);

    const fitToScreen = useCallback(() => {
        const viewport = viewportRef.current;
        if (!page || !viewport) return;
        const rect = viewport.getBoundingClientRect();
        const availableWidth = Math.max(1, rect.width);
        const availableHeight = Math.max(1, rect.height);
        const pageWidth = Number(page.width || 1600);
        const pageHeight = Number(page.height || 900);
        const nextZoom = clamp(Math.min(
            availableWidth / pageWidth,
            availableHeight / pageHeight
        ), MIN_ZOOM, MAX_ZOOM);
        setZoom(nextZoom);
        setPan({
            x: (availableWidth - pageWidth * nextZoom) / 2,
            y: (availableHeight - pageHeight * nextZoom) / 2,
        });
    }, [page]);

    const zoomAroundCenter = (nextZoom) => {
        const boundedZoom = clamp(nextZoom, MIN_ZOOM, MAX_ZOOM);
        const center = {x: viewportSize.width / 2, y: viewportSize.height / 2};
        const contentPoint = {x: (center.x - pan.x) / zoom, y: (center.y - pan.y) / zoom};
        setZoom(boundedZoom);
        setPan(constrainPan({
            x: center.x - contentPoint.x * boundedZoom,
            y: center.y - contentPoint.y * boundedZoom,
        }, boundedZoom));
    };

    const handleToolbarDragStart = (event) => {
        const viewport = viewportRef.current;
        const toolbar = toolbarRef.current;
        if (!viewport || !toolbar) return;
        event.preventDefault();
        event.stopPropagation();
        const viewportRect = viewport.getBoundingClientRect();
        const toolbarRect = toolbar.getBoundingClientRect();
        toolbarDragRef.current = {
            pointerId: event.pointerId,
            offsetX: event.clientX - toolbarRect.left,
            offsetY: event.clientY - toolbarRect.top,
        };
        setToolbarPosition({x: toolbarRect.left - viewportRect.left, y: toolbarRect.top - viewportRect.top});
        event.currentTarget.setPointerCapture?.(event.pointerId);
    };

    const handleToolbarDragMove = (event) => {
        const drag = toolbarDragRef.current;
        const viewport = viewportRef.current;
        const toolbar = toolbarRef.current;
        if (!drag || drag.pointerId !== event.pointerId || !viewport || !toolbar) return;
        event.preventDefault();
        const viewportRect = viewport.getBoundingClientRect();
        const toolbarRect = toolbar.getBoundingClientRect();
        setToolbarPosition({
            x: clamp(event.clientX - viewportRect.left - drag.offsetX, 6, Math.max(6, viewportRect.width - toolbarRect.width - 6)),
            y: clamp(event.clientY - viewportRect.top - drag.offsetY, 6, Math.max(6, viewportRect.height - toolbarRect.height - 6)),
        });
    };

    const handleToolbarDragEnd = (event) => {
        if (toolbarDragRef.current?.pointerId !== event.pointerId) return;
        toolbarDragRef.current = null;
        event.currentTarget.releasePointerCapture?.(event.pointerId);
    };

    const startGestureIfReady = () => {
        const points = Array.from(pointersRef.current.values());
        if (points.length !== 2) return;
        const [a, b] = points;
        gestureRef.current = {
            distance: Math.hypot(b.x - a.x, b.y - a.y) || 1,
            center: {x: (a.x + b.x) / 2, y: (a.y + b.y) / 2},
            zoom,
            pan,
        };
    };

    const handlePointerDown = (event) => {
        if (event.pointerType !== "touch") return;
        const rect = viewportRef.current.getBoundingClientRect();
        const point = {x: event.clientX - rect.left, y: event.clientY - rect.top};
        pointersRef.current.set(event.pointerId, point);
        event.currentTarget.setPointerCapture?.(event.pointerId);
        const interactiveTarget = event.target.closest?.("button, input, select, textarea, a, [role='button']");
        if (pointersRef.current.size === 1 && !interactiveTarget) {
            singlePanRef.current = {pointerId: event.pointerId, start: point, pan, moved: false};
        } else {
            singlePanRef.current = null;
        }
        startGestureIfReady();
    };

    const handlePointerMove = (event) => {
        if (event.pointerType !== "touch" || !pointersRef.current.has(event.pointerId)) return;
        const rect = viewportRef.current.getBoundingClientRect();
        const currentPoint = {x: event.clientX - rect.left, y: event.clientY - rect.top};
        pointersRef.current.set(event.pointerId, currentPoint);
        const points = Array.from(pointersRef.current.values());
        if (points.length === 1 && singlePanRef.current?.pointerId === event.pointerId) {
            const single = singlePanRef.current;
            const dx = currentPoint.x - single.start.x;
            const dy = currentPoint.y - single.start.y;
            if (!single.moved && Math.hypot(dx, dy) > 5) {
                single.moved = true;
                suppressClickRef.current = true;
            }
            if (single.moved) {
                event.preventDefault();
                setPan(constrainPan({x: single.pan.x + dx, y: single.pan.y + dy}, zoom));
            }
            return;
        }
        if (points.length !== 2 || !gestureRef.current) return;
        event.preventDefault();
        suppressClickRef.current = true;
        const [a, b] = points;
        const currentCenter = {x: (a.x + b.x) / 2, y: (a.y + b.y) / 2};
        const currentDistance = Math.hypot(b.x - a.x, b.y - a.y) || 1;
        const start = gestureRef.current;
        const nextZoom = clamp(start.zoom * (currentDistance / start.distance), MIN_ZOOM, MAX_ZOOM);
        const contentPoint = {
            x: (start.center.x - start.pan.x) / start.zoom,
            y: (start.center.y - start.pan.y) / start.zoom,
        };
        setZoom(nextZoom);
        setPan(constrainPan({
            x: currentCenter.x - contentPoint.x * nextZoom,
            y: currentCenter.y - contentPoint.y * nextZoom,
        }, nextZoom));
    };

    const handlePointerEnd = (event) => {
        pointersRef.current.delete(event.pointerId);
        gestureRef.current = null;
        singlePanRef.current = null;
        startGestureIfReady();
        window.setTimeout(() => {
            suppressClickRef.current = false;
        }, 300);
    };

    const handleClickCapture = (event) => {
        if (!suppressClickRef.current) return;
        suppressClickRef.current = false;
        event.preventDefault();
        event.stopPropagation();
    };

    const fetchRuntimeData = async () => {
        try {
            setLoading(true);
            setError("");

            const pageRes = await API.get(`/designer/pages/${id}/`);
            setPage(pageRes.data);

            const objectsRes = await API.get(`/designer/pages/${id}/objects/`);
            setObjects(objectsRes.data?.objects || []);
        } catch (err) {
            console.error("Runtime page load failed:", err);
            setError("Failed to load runtime page.");
        } finally {
            setLoading(false);
        }
    };

    const fetchLatestTags = async () => {
        try {
            const res = await API.get("/latest-tag-values/");
            setLatestTags(res.data || []);
        } catch (err) {
            console.error("Failed to fetch latest tags:", err);
        }
    };

    const handleRuntimeAction = async (targetObject) => {
        const data = targetObject?.data_json || {};

        console.log("Runtime action target:", targetObject);
        console.log("Runtime action data_json:", data);

        // -------------------------------------------------
        // TAG OBJECT CONTROL
        // -------------------------------------------------
        if (targetObject?.type === "tag" && (data.object_mode || "monitor") === "control") {
            const tagName =
                data.tag_name ||
                data.tag ||
                data.real_tag_name ||
                "";

            const controlType =
                targetObject.__controlType ||
                data.control_type ||
                "digital";

            const writeValue =
                targetObject.__controlValue !== undefined
                    ? targetObject.__controlValue
                    : data.write_value;

            if (!tagName) {
                alert("No real tag selected for this tag control.");
                console.warn("Tag control has no tag_name:", targetObject);
                return;
            }

            if (writeValue === "" || writeValue === null || writeValue === undefined) {
                alert("Please enter a value.");
                console.warn("Tag control has empty write value:", targetObject);
                return;
            }

            try {
                console.log("Runtime tag control write:", {
                    tag_name: tagName,
                    value: writeValue,
                    control_type: controlType,
                });

                const res = await API.post("/write-tag/", {
                    tag_name: tagName,
                    value: writeValue,
                    control_type: controlType,
                });

                console.log("Runtime tag control response:", res.data);
                await fetchLatestTags();
            } catch (err) {
                console.error("Failed to write tag from runtime tag control:", err);
                console.error("Server response:", err?.response?.data);
                alert(
                    "Failed to write tag.\n" +
                    JSON.stringify(err?.response?.data || err.message)
                );
            }
            return;
        }

        // -------------------------------------------------
        // BUTTON ACTIONS
        // -------------------------------------------------
        const actionType = data.action_type || data.action || "";

        if (actionType === "tag_write" || actionType === "set_tag") {
            const tagName =
                data.tag_name ||
                data.tag ||
                data.real_tag_name ||
                "";

            const writeValue =
                data.write_value !== undefined
                    ? data.write_value
                    : data.value !== undefined
                        ? data.value
                        : 1;

            const controlType = data.control_type || "digital";

            if (!tagName) {
                alert("No real tag selected for this button.");
                console.warn("Button has no tag_name:", targetObject);
                return;
            }

            try {
                console.log("Runtime button tag write:", {
                    tag_name: tagName,
                    value: writeValue,
                    control_type: controlType,
                });

                const res = await API.post("/write-tag/", {
                    tag_name: tagName,
                    value: writeValue,
                    control_type: controlType,
                });

                console.log("Write tag response:", res.data);
                await fetchLatestTags();
            } catch (err) {
                console.error("Failed to write tag from runtime button:", err);
                console.error("Server response:", err?.response?.data);
                alert(
                    "Failed to write tag.\n" +
                    JSON.stringify(err?.response?.data || err.message)
                );
            }
            return;
        }

        if (actionType === "link" || actionType === "navigate") {
            const url = data.url || data.link || "";
            const openInNewTab = data.open_in_new_tab ?? true;

            if (!url) {
                alert("No URL configured.");
                console.warn("Button has no URL:", targetObject);
                return;
            }

            console.log("Opening URL:", url);

            if (openInNewTab) {
                window.open(url, "_blank", "noopener,noreferrer");
            } else {
                window.location.href = url;
            }
            return;
        }

        alert("This object has no valid action configured.");
        console.warn("Unknown runtime action:", targetObject);
    };

    useEffect(() => {
        fetchRuntimeData();
        fetchLatestTags();

        const interval = setInterval(fetchLatestTags, 3000);
        return () => clearInterval(interval);
    }, [id]);

    useEffect(() => {
        const previousBodyOverflow = document.body.style.overflow;
        const previousBodyOverscroll = document.body.style.overscrollBehavior;
        const previousHtmlOverscroll = document.documentElement.style.overscrollBehavior;
        document.body.style.overflow = "hidden";
        document.body.style.overscrollBehavior = "none";
        document.documentElement.style.overscrollBehavior = "none";
        return () => {
            document.body.style.overflow = previousBodyOverflow;
            document.body.style.overscrollBehavior = previousBodyOverscroll;
            document.documentElement.style.overscrollBehavior = previousHtmlOverscroll;
        };
    }, []);

    useEffect(() => {
        document.body.classList.add("runtime-header-hidden");
        return () => document.body.classList.remove("runtime-header-hidden");
    }, []);

    useEffect(() => {
        const element = viewportRef.current;
        if (!element) return undefined;
        const observer = new ResizeObserver(([entry]) => setViewportSize((current) => {
            const width = Math.round(entry.contentRect.width);
            const height = Math.round(entry.contentRect.height);
            return current.width === width && current.height === height ? current : {width, height};
        }));
        observer.observe(element);
        return () => observer.disconnect();
    }, [page]);

    useEffect(() => {
        if (!page || !viewportSize.width || !viewportSize.height) return;
        const fitKey = `${page.id}:${viewportSize.width}x${viewportSize.height}`;
        if (fittedPageRef.current === fitKey) return;
        fittedPageRef.current = fitKey;
        fitToScreen();
    }, [page, viewportSize, fitToScreen]);

    useEffect(() => {
        if (!toolbarPosition || !toolbarRef.current || !viewportSize.width || !viewportSize.height) return;
        const toolbarRect = toolbarRef.current.getBoundingClientRect();
        const next = {
            x: clamp(toolbarPosition.x, 6, Math.max(6, viewportSize.width - toolbarRect.width - 6)),
            y: clamp(toolbarPosition.y, 6, Math.max(6, viewportSize.height - toolbarRect.height - 6)),
        };
        if (next.x !== toolbarPosition.x || next.y !== toolbarPosition.y) setToolbarPosition(next);
    }, [toolbarPosition, viewportSize]);

    useEffect(() => {
        if (toolbarPosition) localStorage.setItem("runtimeZoomToolbarPosition", JSON.stringify(toolbarPosition));
        else localStorage.removeItem("runtimeZoomToolbarPosition");
    }, [toolbarPosition]);

    if (loading) {
        return <div style={{padding: 20}}>Loading...</div>;
    }

    if (error || !page) {
        return (
            <div style={{padding: 20, color: "red"}}>
                {error || "Page not found."}
            </div>
        );
    }

    return <div className="runtime-zoom-shell" style={{background: page.background_color || "#ffffff"}}>
        <div ref={toolbarRef} className="runtime-zoom-toolbar" aria-label="Runtime zoom controls" style={toolbarPosition ? {left: `${toolbarPosition.x}px`, top: `${toolbarPosition.y}px`, right: "auto"} : undefined}>
            <button type="button" className="runtime-toolbar-drag" onPointerDown={handleToolbarDragStart} onPointerMove={handleToolbarDragMove} onPointerUp={handleToolbarDragEnd} onPointerCancel={handleToolbarDragEnd} onDoubleClick={() => setToolbarPosition(null)} aria-label="Move zoom controls" title="Drag to move · double-click to reset">⠿</button>
            <button type="button" className="runtime-menu-toggle" onClick={() => window.dispatchEvent(new Event("aqueris-runtime-nav-toggle"))} aria-label="Open menu" title="Menu">
                <span aria-hidden="true" /><span aria-hidden="true" /><span aria-hidden="true" />
            </button>
            <button type="button" onClick={() => zoomAroundCenter(zoom / 1.2)} aria-label="Zoom out">−</button>
            <button type="button" className="runtime-zoom-value" onClick={fitToScreen} title="Fit page to screen">{Math.round(zoom * 100)}%</button>
            <button type="button" onClick={() => zoomAroundCenter(zoom * 1.2)} aria-label="Zoom in">+</button>
            <button type="button" className="runtime-fit-btn" onClick={fitToScreen}>Fit</button>
        </div>
        <div
            ref={viewportRef}
            className="runtime-zoom-viewport"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerEnd}
            onPointerCancel={handlePointerEnd}
            onClickCapture={handleClickCapture}
        >
            <div className="runtime-zoom-stage" style={{
                width: `${page.width}px`,
                height: `${page.height}px`,
                transform: `translate3d(${pan.x}px, ${pan.y}px, 0) scale(${zoom})`,
            }}>
                <DesignerCanvas
                    page={page}
                    objects={objects}
                    latestTags={latestTags}
                    selectedObjectId={null}
                    setSelectedObjectId={() => {}}
                    updateObjectPosition={() => {}}
                    updateObjectRect={() => {}}
                    updateObjectDataField={() => {}}
                    onButtonAction={handleRuntimeAction}
                    readOnly={true}
                    hideCanvasPadding={true}
                    viewportManaged={true}
                />
            </div>
        </div>
    </div>;
};

export default RuntimePage;
